import { useId, useState } from "react";
import type { Jurisdiction } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/Chip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Input } from "@/components/ui/input";
import { Note } from "@/components/ui/Note";
import { Select } from "@/components/ui/select";
import { localToday } from "@/lib/day";
import { TypeAhead } from "@/components/ui/TypeAhead";
import { useInventorMatches } from "@/hooks/use-clients";
import { TextField } from "./ClientField";
import { UNASSIGNED_TITLE } from "./paths";

/** The offices a draft can be prepared for, as the attorney reads them. The pilot offers one. */
export const JURISDICTION_LABEL: Record<Jurisdiction, string> = {
  USPTO: "USPTO",
};
const JURISDICTIONS = Object.keys(JURISDICTION_LABEL) as Jurisdiction[];

/** An inventor as the form holds it: the id it was served with, or none for one added here. */
interface InventorEntry {
  inventor_id: string | null;
  name: string;
}

/** A draft's four details as the modal holds them while the attorney types. */
export interface DetailsForm {
  title: string;
  inventors: InventorEntry[];
  jurisdiction: Jurisdiction;
  deadline: string;
}

export const EMPTY_DETAILS: DetailsForm = {
  title: "",
  inventors: [],
  jurisdiction: "USPTO",
  deadline: "",
};

/** A deadline is today or later, unless it is the one already kept, which may have passed. */
export function deadlineAllowed(
  deadline: string,
  kept: string | null = null,
): boolean {
  return deadline === kept || deadline >= localToday();
}

/** Whether every detail is given, a name of spaces alone not counting. */
export function isComplete(form: DetailsForm): boolean {
  return (
    form.title.trim() !== "" &&
    form.inventors.length > 0 &&
    form.deadline !== ""
  );
}

const sameName = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** The inventors on the draft so far, each an entry that can be taken back out. */
function AddedInventors({
  inventors,
  onRemove,
}: {
  inventors: readonly InventorEntry[];
  onRemove: (entry: InventorEntry) => void;
}) {
  if (inventors.length === 0) return null;
  return (
    <ul aria-label="Inventors added" className="mt-2 flex flex-wrap gap-1.5">
      {inventors.map((entry) => (
        <li key={entry.inventor_id ?? entry.name}>
          <Chip
            onRemove={() => {
              onRemove(entry);
            }}
          >
            {entry.name}
          </Chip>
        </li>
      ))}
    </ul>
  );
}

/**
 * One or more inventors: a name typed and added, or one found on record as the attorney types,
 * becomes an entry that can be taken back out. The type-ahead lists the draft's own client's
 * inventors first, then every other client's, each with its client.
 */
function InventorsField({
  inventors,
  clientId,
  onChange,
}: {
  inventors: readonly InventorEntry[];
  clientId: string | null;
  onChange: (inventors: InventorEntry[]) => void;
}) {
  const [typed, setTyped] = useState("");
  const inputId = useId();
  const hintId = useId();
  const matches = useInventorMatches(typed, clientId);
  const options = matches.inventors
    .filter(
      (match) =>
        !inventors.some((inventor) => sameName(inventor.name, match.name)),
    )
    .map((match) => ({
      key: `${match.name}|${match.client_id ?? ""}`,
      label: match.name,
      detail: match.client_name ?? UNASSIGNED_TITLE,
    }));

  function include(name: string) {
    if (!inventors.some((inventor) => sameName(inventor.name, name))) {
      onChange([...inventors, { inventor_id: null, name }]);
    }
    setTyped("");
  }

  return (
    <div>
      <FieldLabel htmlFor={inputId} look="sentence">
        {inventors.length > 1 ? "Inventors" : "Inventor"}
      </FieldLabel>
      <TypeAhead
        inputId={inputId}
        value={typed}
        onValue={setTyped}
        options={options}
        listName="Inventors on record"
        onChoose={(option) => {
          include(option.label);
        }}
        onAdd={include}
        addName="Add inventor"
        describedBy={hintId}
        placeholder="e.g. Ada Park"
        pending={matches.pending}
      />
      <Note id={hintId} hint className="mt-1.5">
        Name every inventor on the draft. Start typing to find one already on
        record, or add a new name.
      </Note>
      <AddedInventors
        inventors={inventors}
        onRemove={(entry) => {
          onChange(inventors.filter((inventor) => inventor !== entry));
        }}
      />
    </div>
  );
}

/** Where the draft is filed; the pilot offers one office, so the choice is made for the attorney. */
function JurisdictionField({
  value,
  onChange,
}: {
  value: Jurisdiction;
  onChange: (jurisdiction: Jurisdiction) => void;
}) {
  const selectId = useId();
  return (
    <div>
      <FieldLabel htmlFor={selectId} look="sentence">
        Primary Jurisdiction
      </FieldLabel>
      <Select
        id={selectId}
        size="roomy"
        value={value}
        onChange={(event) => {
          const chosen = JURISDICTIONS.find(
            (office) => office === event.target.value,
          );
          if (chosen !== undefined) onChange(chosen);
        }}
      >
        {JURISDICTIONS.map((office) => (
          <option key={office} value={office}>
            {JURISDICTION_LABEL[office]}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** The day the draft must be submitted by, on the browser's own calendar, which starts at today. */
function DeadlineField({
  value,
  onChange,
}: {
  value: string;
  onChange: (deadline: string) => void;
}) {
  const inputId = useId();
  const hintId = useId();
  return (
    <div>
      <FieldLabel htmlFor={inputId} look="sentence">
        Filing deadline
      </FieldLabel>
      <Input
        id={inputId}
        type="date"
        size="roomy"
        min={localToday()}
        value={value}
        aria-describedby={hintId}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <Note id={hintId} hint className="mt-1.5">
        The date the draft must be submitted by.
      </Note>
    </div>
  );
}

/**
 * The four details a draft is made with, as one set of fields: the invention
 * name, its inventors with the client's on record offered, where it is filed
 * and by when. The new draft and edit modals each draw these and own the save.
 */
function DraftDetailsFields({
  form,
  onChange,
  clientId,
}: {
  form: DetailsForm;
  onChange: (change: Partial<DetailsForm>) => void;
  clientId: string | null;
}) {
  return (
    <>
      <TextField
        label="Working title"
        placeholder="e.g. Rainfast coating for timber"
        value={form.title}
        onChange={(title) => {
          onChange({ title });
        }}
      />
      <InventorsField
        inventors={form.inventors}
        clientId={clientId}
        onChange={(inventors) => {
          onChange({ inventors });
        }}
      />
      <JurisdictionField
        value={form.jurisdiction}
        onChange={(jurisdiction) => {
          onChange({ jurisdiction });
        }}
      />
      <DeadlineField
        value={form.deadline}
        onChange={(deadline) => {
          onChange({ deadline });
        }}
      />
    </>
  );
}

/**
 * The modal both the new draft and the edit draw: a title, a sentence, the four
 * fields and a footer whose one act is theirs. The form checks the deadline
 * itself, since a kept deadline may already have passed and the browser's own
 * check would refuse it under a minimum of today.
 */
export function DraftDetailsDialog({
  open = true,
  title,
  description,
  form,
  onChange,
  clientId,
  submitLabel,
  ready,
  onSubmit,
  onClose,
}: {
  open?: boolean;
  title: string;
  description: string;
  form: DetailsForm;
  onChange: (change: Partial<DetailsForm>) => void;
  /** The client the draft is under, whose inventors the type-ahead lists first. */
  clientId: string | null;
  submitLabel: string;
  ready: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const sentenceId = useId();
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent aria-describedby={sentenceId}>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription id={sentenceId}>{description}</DialogDescription>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) onSubmit();
          }}
        >
          <DraftDetailsFields
            form={form}
            onChange={onChange}
            clientId={clientId}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
